import React, { useEffect, useRef, useState } from 'react';
import { useQuery } from '@apollo/client/react';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { CompositeFilterDescriptor, getter } from '@progress/kendo-data-query';
import { GridSelectionChangeEvent } from '@progress/kendo-react-grid';
import { SplitterOnChangeEvent, SplitterPaneProps } from '@progress/kendo-react-layout';
import { HTTPError } from 'ky';
import debounce from 'lodash/debounce';
import EmailActionsDialog from './dialogs/actions/email-actions-dialog';
import EditSiteObservationDialog from './dialogs/edit-site-observation-dialog';
import { checkFiltersApplied } from './dialogs/helpers/filter-helper';
import styles from './style.module.scss';
import ApiGrid, { ApiGridHandle } from '@components-clean/core/api-grid';
import BulkUpdateDialog from '@components-clean/core/bulk-update-dialog/bulk-update-dialog';
import ConfirmationDialog from '@components-clean/core/confirmation-dialog';
import CoreSplitter from '@components-clean/core/core-splitter';
import { IntegrationsConfig } from '@components-clean/core/grid/grid-menu/grid-settings-menu/grid-integrations-menu';
import { GridStatusProps } from '@components-clean/core/grid/grid-status';
import { GridHeaderTitle, RowMenuAction } from '@components-clean/core/grid/grid.interface';
import { InfoField } from '@components-clean/core/info-field';
import { ActionsEmailPreviewDto, ApiGridParams, ObservationRecord } from '@interfaces/central-monitoring';
import { Action, IntegrationState } from '@interfaces-clean/actions.interface';
import { SelectedFieldValuesProps } from '@interfaces-clean/bulk-update.interface';
import {
    ChipHeaders,
    EnumConverter,
    FilterContext,
    MonitoringToolKitTables,
    Observation,
    ObservationProps,
    ObservationsSiteDto,
    ObservationType,
    SelectedRowsProps,
} from '@interfaces-clean/observations.interface';
import { SiteReviewLog } from '@interfaces-clean/review-logs.interface';
import { AutomationTypes } from '@pages-clean/central-monitoring/dataset-hub/interfaces/hub-dataset.interface';
import AddActionDialog, {
    AddActionForm,
} from '@pages-clean/central-monitoring/monitoring-toolkit/tabs/dialogs/actions/add-action-dialog';
import DeleteActionDialog from '@pages-clean/central-monitoring/monitoring-toolkit/tabs/dialogs/actions/delete-action-dialog';
import EditActionDialog, {
    EditActionForm,
} from '@pages-clean/central-monitoring/monitoring-toolkit/tabs/dialogs/actions/edit-action-dialog';
import AddReviewLogDialog, {
    AddReviewLog,
} from '@pages-clean/central-monitoring/monitoring-toolkit/tabs/dialogs/add-review-log-dialog';
import AddSiteObservationDialog, {
    FormInputs,
} from '@pages-clean/central-monitoring/monitoring-toolkit/tabs/dialogs/add-site-observation-dialog';
import DeleteObservationDialog from '@pages-clean/central-monitoring/monitoring-toolkit/tabs/dialogs/delete-observation-dialog';
import DeleteReviewLogDialog from '@pages-clean/central-monitoring/monitoring-toolkit/tabs/dialogs/delete-review-log-dialog';
import { EditForm } from '@pages-clean/central-monitoring/monitoring-toolkit/tabs/dialogs/observation.interface';
import EditSiteReviewLogDialog, {
    EditReviewLogForm,
} from '@pages-clean/central-monitoring/monitoring-toolkit/tabs/dialogs/site/review-logs/edit-site-review-log-dialog';
import { assumeUtc } from '@utils-clean/date-time-helpers';
import { useBoolean, useLocalState, useTranslation } from '@hooks/index';
import { GridFullView, GridViewOptions, SplitterOrientation } from '@utils/constants';
import {
    stringDateWithoutTimeFromCurrentUtcDate,
    stringDateWithoutTimeFromLocal,
    stringDateWithoutTimeFromUtc,
} from '@utils/convertor';
import { ShowError, ShowMessage } from '@utils/notification';
import { hasIntegrationConfig_OpraQueries } from '@queries/generated/org-admin.generated';
import { HAS_INTEGRATION_CONFIG } from '@queries/org-admin';
import ActionsApi from '@apis/bff-api/central-monitoring/actions';
import ObservationsApi from '@apis/bff-api/central-monitoring/observation';
import SiteReviewLogsApi from '@apis/bff-api/central-monitoring/site-review-log';
import IntegrationsApi from '@apis/bff-api/integrations';

type SelectionState = {
    recordId?: string;
    reviewLogRecordId?: string;
    selectedItemsCount: number;
    childGridParams: Extract<ApiGridParams, { grid: 'Observations' | 'Actions' | 'ReviewLog' }>;
    selectedField: string;
    bulkUpdateRows: SelectedRowsProps[];
    selectedChildChipHeader: ChipHeaders;
    childTableName: string;
    childMenuActionItems: RowMenuAction[] | undefined;
    selectedChildGridTitle: GridHeaderTitle;
    selectedItemId?: string | null;
    actionRecordId?: string;
};

type ConfirmationDialogType = {
    show: boolean;
    onSubmit: () => void;
};

const getExportChildFileName = (
    grid: ApiGridParams['grid'],
    studyNumber: string,
    selectedChildChipHeader: ChipHeaders,
    selectedParentChipHeader: ChipHeaders,
    recordId?: string,
    reviewLogRecordId?: string,
    actionRecordId?: string,
): string => {
    const utcNow = stringDateWithoutTimeFromCurrentUtcDate('DD-MMM-yyyy');
    switch (grid) {
        case 'Observations':
            return `Site Observation ID ${recordId}_${studyNumber}_${utcNow}`;
        case 'Actions':
            if (selectedParentChipHeader === ChipHeaders.ReviewLogView) {
                return `Actions for Review Log ID ${reviewLogRecordId}_${studyNumber}_${utcNow}`;
            }
            return `Actions for Site Observation ID ${recordId}_${studyNumber}_${utcNow}`;
        case 'ReviewLog':
            if (selectedChildChipHeader === ChipHeaders.ReviewLogHistory) {
                return `Review Log History for Site Observation ID ${recordId}_${studyNumber}_${utcNow}`;
            }
            if (selectedParentChipHeader === ChipHeaders.ActionView) {
                return `Site Review Logs_${studyNumber}_Action ID ${actionRecordId}_${utcNow}`;
            }
            return `Site Review Logs_${studyNumber}_Observation ID ${recordId}_${utcNow}`;
        default:
            return `Site Observation ID ${recordId}_${studyNumber}_${utcNow}`;
    }
};

const SiteObservations = ({ orgKey, studyRef, studyNumber, canEdit, isHalfScreen }: ObservationProps) => {
    const showDeleteDialog = useBoolean(false);
    const showAddDialog = useBoolean(false);
    const showAddActionDialog = useBoolean(false);
    const showEditDialog = useBoolean(false);
    const showActionDeleteDialog = useBoolean(false);
    const showEditReviewLogDialog = useBoolean(false);
    const showAddReviewLogDialog = useBoolean(false);
    const showBulkUpdateDialog = useBoolean(false);
    const showBulkUpdateWarningDialog = useBoolean(false);
    const isChildGridSelection = useBoolean(false);
    const [dataRefreshDialogState, setDataRefreshDialogState] = useState<ConfirmationDialogType>({
        show: false,
        onSubmit: () => {},
    });

    const [bulkUpdateFields, setBulkUpdateFields] = useState<SelectedFieldValuesProps[]>([]);

    const { trigger: updateReviewLog } = SiteReviewLogsApi.useUpdate(studyRef);

    const showDeleteReviewLogDialog = useBoolean(false);

    const { trigger: saveObservation } = ObservationsApi.useSave(studyRef);
    const { trigger: updateObservation } = ObservationsApi.useUpdate(studyRef);
    const { trigger: saveAction } = ActionsApi.useSave(studyRef);
    const { trigger: updateAction } = ActionsApi.useUpdate(studyRef);
    const { trigger: saveReviewLog } = SiteReviewLogsApi.useSave(studyRef);
    const { trigger: bulkUpdateObservations } = ObservationsApi.useBulkUpdate(studyRef);
    const { trigger: bulkUpdateReviewLogs } = SiteReviewLogsApi.useBulkUpdate(studyRef);
    const { trigger: bulkUpdateActions } = ActionsApi.useBulkUpdate(studyRef);
    const { trigger: HasClosedBeforeLatestReview } = ObservationsApi.useHasClosedBeforeLatestReview(studyRef);

    const [reviewLog, setReviewLog] = useState<SiteReviewLog | null>();
    const {
        trigger: addAndReviewDialog,
        data: reviewLogData,
        isMutating: loadingReviewLog,
    } = SiteReviewLogsApi.useGet(studyRef);
    const handleReview = async (reviewLogId: string) => {
        await addAndReviewDialog(reviewLogId);
    };

    const { state } = useLocalState();
    const study = state?.selectedStudy;

    useEffect(() => {
        if (reviewLogData && reviewLogData.data && !loadingReviewLog) {
            const review = reviewLogData.data[0] as SiteReviewLog;
            // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
            // eslint-disable-next-line @eslint-react/set-state-in-effect
            setReviewLog(review);
            showEditReviewLogDialog.setTrue();
        }
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/exhaustive-deps
    }, [reviewLogData, loadingReviewLog]);

    const { t } = useTranslation(['common', 'cm']);
    const onAddClose = () => {
        showAddDialog.setFalse();
    };
    const onDeleteClose = () => {
        showDeleteDialog.setFalse();
    };
    const onEditClose = () => {
        showEditDialog.setFalse();
    };
    const parentGridRef = useRef<ApiGridHandle>(null);
    const childGridRef = useRef<ApiGridHandle>(null);

    const [selectedTitle, setSelectedTitle] = useState<GridHeaderTitle | null>({
        id: ChipHeaders.ObservationsView,
        label: 'Observations',
    });
    const [tableName, setTableName] = useState<string>(MonitoringToolKitTables.Observations);
    const [selectedParentChipHeader, setSelectedParentChipHeader] = useState<ChipHeaders>(ChipHeaders.ObservationsView);
    const [parentRecordSelectMessage, setParentRecordSelectMessage] = useState<string>(() =>
        t('cm:observationsGrid:selectObservationInfo'),
    );
    const [parentMultipleRecordSelectMessage, setParentMultipleRecordSelectMessage] = useState<string>(() =>
        t('cm:observationsGrid:multipleSelectObservationInfo'),
    );

    const [updateParentGridParams, setUpdateParentGridParams] = useState<ApiGridParams>({
        grid: 'Observations',
        studyRef,
        level: ObservationType.Site,
        observationId: null,
        isChild: false,
    });
    const actionsCountRef = useRef<number>(0);
    const reviewLogsCountRef = useRef<number>(0);
    const [siteReviewLog, setSiteReviewLog] = useState<SiteReviewLog | null>();
    const mainPanelHeaders = [
        {
            id: ChipHeaders.ObservationsView,
            label: 'Observations',
        },
        {
            id: ChipHeaders.ReviewLogView,
            label: 'Review Logs',
        },
        {
            id: ChipHeaders.ActionView,
            label: 'Actions',
        },
    ];
    const [exportFileName, setExportFileName] = useState<string>(
        () => `Site Observations_${studyNumber}_${stringDateWithoutTimeFromCurrentUtcDate('DD-MMM-yyyy')}`,
    );

    const [subPanelHeaders, setSubPanelHeaders] = useState([
        { id: ChipHeaders.ReviewLogView, label: 'Review Logs' },
        {
            id: ChipHeaders.ActionView,
            label: 'Actions',
        },
    ]);
    const [observation, setObservation] = useState<Observation>();
    const showFilterAppliedDialog = useBoolean(false);
    const parentFilterRef = useRef<CompositeFilterDescriptor | null>(null);
    const childFilterRef = useRef<CompositeFilterDescriptor | null>(null);
    const parentChipRef = useRef<ChipHeaders>(ChipHeaders.ObservationsView);
    const childChipRef = useRef<ChipHeaders>(ChipHeaders.ReviewLogView);

    const [action, setAction] = useState<Action | null>();
    const showEditActionDialog = useBoolean(false);

    const { trigger: pushIntegrations } = IntegrationsApi.usePush(studyRef);
    const { trigger: retryIntegrations } = IntegrationsApi.useRetry(studyRef);
    const { data: hasIntegrationConfig } = useQuery<hasIntegrationConfig_OpraQueries>(HAS_INTEGRATION_CONFIG, {
        skip: !study?.integrationActive,
    });

    const gridStatusSetup: GridStatusProps = {
        automationStatusSetup: {
            orgKey,
            studyRef,
            automationType: AutomationTypes.Site,
        },
        integrationStatusSetup: {
            studyRef,
            observationType: ObservationType.Site,
        },
    };

    const reviewLogMenuActionItems: RowMenuAction[] = [
        {
            id: 1,
            label: t('cm:reviewLogGrid.editReviewLog'),
            onClick: (dataItem) => {
                const review = dataItem as SiteReviewLog;
                setReviewLog(review);
                showEditReviewLogDialog.setTrue();
            },
            icon: ['far', 'pen-to-square'],
            disabled: !canEdit,
        },
        {
            id: 2,
            label: t('cm:reviewLogGrid.deleteReviewLog'),
            onClick: (dataItem) => {
                const log = dataItem as SiteReviewLog;
                setSiteReviewLog(log);
                showDeleteReviewLogDialog.setTrue();
            },
            icon: ['fas', 'trash-xmark'],
            colorProp: 'Red',
            disabled: !canEdit,
            rowDisabled: (dataItem: unknown) => {
                const item = dataItem as SiteReviewLog;
                return (
                    item.type !== 'Manual' ||
                    item?.is_latest_dataset_date !== 'Yes' ||
                    item?.has_integrated_actions === 'Yes'
                );
            },
            showMenuItem: (dataItem: unknown) => {
                const item = dataItem as SiteReviewLog;
                return item.type === 'Manual' && item.is_first_log === 'No';
            },
        },
    ];

    const [selectionState, setSelectionState] = useState<SelectionState>({
        selectedItemsCount: 0,
        childGridParams: {
            grid: 'ReviewLog',
            studyRef,
            level: ObservationType.Site,
            observationId: null,
            reviewLogId: null,
            isChild: true,
            isHistory: false,
        },
        selectedField: '',
        bulkUpdateRows: [],
        selectedChildChipHeader: ChipHeaders.ReviewLogView,
        childTableName: MonitoringToolKitTables.SiteReviewLogs,
        childMenuActionItems: reviewLogMenuActionItems,
        selectedChildGridTitle: {
            id: ChipHeaders.ReviewLogView,
            label: 'Review Logs',
        },
    });

    const observationMenuActionItems = (isParentGrid: boolean = true): RowMenuAction[] => [
        {
            id: 1,
            label: t('cm:observationsGrid.editObservation'),
            onClick: (dataItem: unknown) => {
                const obs = dataItem as Observation;
                setObservation(obs);
                showEditDialog.setTrue();
            },
            icon: ['far', 'pen-to-square'],
            disabled: !canEdit,
        },
        ...(isParentGrid
            ? [
                  {
                      id: 2,
                      label: t('cm:observationsGrid.addAction'),
                      onClick: (dataItem: unknown) => {
                          const obs = dataItem as Observation;
                          checkFiltersApplied(
                              FilterContext.AddAction,
                              parentChipRef,
                              childChipRef,
                              parentFilterRef,
                              childFilterRef,
                              showFilterAppliedDialog,
                          );
                          setObservation(obs);
                          showAddActionDialog.setTrue();
                      },
                      icon: ['far', 'add'] as IconProp,
                      rowDisabled: (dataItem: unknown) => {
                          const item = dataItem as ObservationsSiteDto;
                          return item.status === 'Closed';
                      },
                      disabled: !canEdit,
                  },
              ]
            : []),
        ...(isParentGrid
            ? [
                  {
                      id: 3,
                      label: t('cm:observationsGrid.addReviewLog'),
                      rowDisabled: (dataItem: unknown) => {
                          const item = dataItem as ObservationsSiteDto;
                          return (
                              item.type !== 'Manual' ||
                              item.status === 'Closed' ||
                              item.review_log_created_for_latest_dataset === 'Yes'
                          );
                      },
                      onClick: (dataItem: unknown) => {
                          const obs = dataItem as Observation;
                          checkFiltersApplied(
                              FilterContext.AddReviewLog,
                              parentChipRef,
                              childChipRef,
                              parentFilterRef,
                              childFilterRef,
                              showFilterAppliedDialog,
                          );
                          setObservation(obs);
                          showAddReviewLogDialog.setTrue();
                      },
                      icon: ['far', 'add'] as IconProp,
                      disabled: !canEdit,
                      showMenuItem: (dataItem: unknown) => {
                          const item = dataItem as ObservationsSiteDto;
                          return item.type !== 'System';
                      },
                  },
              ]
            : []),
        {
            id: 4,
            label: t('cm:observationsGrid.deleteObservation'),
            onClick: (dataItem: unknown) => {
                const obs = dataItem as Observation;
                setObservation(obs);
                actionsCountRef.current = Number(obs.open_actions);
                reviewLogsCountRef.current = Number(obs.reviews);
                setSelectionState((old) => ({
                    ...old,
                    recordId: obs.record_id,
                }));
                showDeleteDialog.setTrue();
            },
            icon: ['fas', 'trash-xmark'],
            rowDisabled: (dataItem: unknown) => {
                const item = dataItem as ObservationsSiteDto;
                return item.type !== 'Manual' || item.has_integrated_actions === 'Yes';
            },
            colorProp: 'Red',
            disabled: !canEdit,
            showMenuItem: (dataItem: unknown) => {
                const item = dataItem as ObservationsSiteDto;
                return item.type !== 'System';
            },
        },
    ];
    const actionMenuActionItems: RowMenuAction[] = [
        {
            id: 1,
            label: t('cm:actionGrid.editAction'),
            onClick: (dataItem) => {
                const act = dataItem as Action;
                setAction(act);
                showEditActionDialog.setTrue();
            },
            disabled: !canEdit,
            icon: ['far', 'pen-to-square'],
        },
        {
            id: 2,
            label: t('cm:actionGrid.deleteAction'),
            onClick: (dataItem) => {
                const act = dataItem as Action;
                setAction(act);
                showActionDeleteDialog.setTrue();
            },
            disabled: !canEdit,
            rowDisabled: (dataItem) => {
                const act = dataItem! as Action;
                return act.integration_enabled === IntegrationState.PushRequested;
            },
            icon: ['fas', 'trash-xmark'],
            colorProp: 'Red',
        },
    ];

    const observationFields = [
        {
            field: 'closedDate',
            type: 'date',
            required: false,
        },
        {
            field: 'comments',
            type: 'text',
            required: false,
        },
        {
            field: 'ownerRef',
            type: 'ownerLov',
            required: false,
        },
        {
            field: 'rootCause',
            type: 'text',
            required: false,
        },
    ];
    const actionFields = [
        {
            field: 'completedDate',
            type: 'date',
            required: false,
        },
        {
            field: 'dueDate',
            type: 'date',
            required: false,
        },
        {
            field: 'ownerRef',
            type: 'ownerLov',
            required: false,
        },
        {
            field: 'requestedDate',
            type: 'date',
            required: true,
        },
        {
            field: 'resolution',
            type: 'text',
            required: false,
        },
        {
            field: 'responsibleFunction',
            type: 'responsibleFunctionLov',
            required: false,
        },
    ];

    const ReviewLogFields = [
        {
            field: 'comments',
            type: 'text',
            required: false,
        },
        {
            field: 'decisionId',
            type: 'decisionLov',
            required: false,
        },
    ];

    const handleOnBulkUpdateClicked = () => {
        if (selectionState.bulkUpdateRows.length > 0 && !isChildGridSelection.value) {
            showBulkUpdateDialog.setTrue();
        }
    };

    const handleOnChildUpdateClicked = () => {
        if (selectionState.bulkUpdateRows.length > 0 && isChildGridSelection.value) {
            showBulkUpdateDialog.setTrue();
        }
    };

    const onSubmitBulkUpdate = async (data: SelectedFieldValuesProps[]) => {
        try {
            let clearClosedDate = false;
            let clearCompletedDate = false;
            let clearDueDate = false;
            let clearRequestedDate = false;
            let refreshObs = false;

            const getFieldValue = (field: string, dropDownName?: boolean) => {
                const fieldObj = data.find((f) => f.field === field);
                if (fieldObj) {
                    if (fieldObj.type === 'date' && fieldObj.value !== '' && fieldObj.value != null) {
                        return assumeUtc(new Date(fieldObj.value));
                    }
                    if (fieldObj.type === 'date' && fieldObj.value === '') {
                        switch (field) {
                            case 'closedDate':
                                clearClosedDate = true;
                                break;
                            case 'completedDate':
                                clearCompletedDate = true;
                                break;
                            case 'dueDate':
                                clearDueDate = true;
                                break;
                            case 'requestedDate':
                                clearRequestedDate = true;
                                break;
                            default:
                                break;
                        }

                        return null;
                    }
                    if (fieldObj.type === 'decisionLov' && dropDownName === true && fieldObj.text !== '') {
                        return fieldObj.text;
                    }
                    return fieldObj.value;
                }

                return null;
            };

            let response: boolean = false;
            if (!isChildGridSelection.value && tableName === MonitoringToolKitTables.Observations) {
                const closedDateValue = getFieldValue('closedDate')?.toString();

                response = await bulkUpdateObservations({
                    observationIds: selectionState.bulkUpdateRows.map((item) => item.id),
                    closedDate: closedDateValue ? stringDateWithoutTimeFromLocal(closedDateValue) : undefined,
                    ownerRef: getFieldValue('ownerRef'),
                    comments: getFieldValue('comments'),
                    rootCause: getFieldValue('rootCause'),
                    clearClosedDate,
                });
            } else if (
                (!isChildGridSelection.value && tableName === MonitoringToolKitTables.SiteReviewLogs) ||
                (isChildGridSelection.value && selectionState.childTableName === MonitoringToolKitTables.SiteReviewLogs)
            ) {
                const rawValue = getFieldValue('decisionId');
                response = await bulkUpdateReviewLogs({
                    reviewLogIds: selectionState.bulkUpdateRows.map((item) => item.id),
                    decisionId: rawValue === null ? null : Number(rawValue),
                    decision: getFieldValue('decisionId', true),
                    comments: getFieldValue('comments'),
                });

                refreshObs = data.some((x) => x.field === 'decisionId');
            } else if (
                (!isChildGridSelection.value && tableName === MonitoringToolKitTables.Actions) ||
                (isChildGridSelection.value && selectionState.childTableName === MonitoringToolKitTables.Actions)
            ) {
                const completedDateValue = getFieldValue('completedDate')?.toString();
                const dueDateValue = getFieldValue('dueDate')?.toString();
                const requestedDateValue = getFieldValue('requestedDate')?.toString();

                response = await bulkUpdateActions({
                    actionIds: selectionState.bulkUpdateRows.map((item) => item.id),
                    completedDate: completedDateValue ? stringDateWithoutTimeFromLocal(completedDateValue) : undefined,
                    dueDate: dueDateValue ? stringDateWithoutTimeFromLocal(dueDateValue) : undefined,
                    ownerRef: getFieldValue('ownerRef'),
                    requestedDate: stringDateWithoutTimeFromLocal(requestedDateValue!)!,
                    resolution: getFieldValue('resolution'),
                    studyFunctionRef: getFieldValue('responsibleFunction'),
                    clearCompletedDate,
                    clearDueDate,
                    clearRequestedDate,
                });

                refreshObs = data.some((x) => x.field === 'completedDate');
            }
            if (response) {
                if (!isChildGridSelection.value) {
                    parentGridRef?.current?.refreshData(false);
                    if (selectionState.selectedChildChipHeader === ChipHeaders.ObservationsView && refreshObs)
                        childGridRef?.current?.refreshData(false);
                } else {
                    childGridRef?.current?.refreshData(false);
                    if (selectedParentChipHeader === ChipHeaders.ObservationsView && refreshObs)
                        parentGridRef?.current?.refreshData(false);
                }
                ShowMessage({ text: t('actionsSuccessfulToast'), type: 'success' });
            } else {
                ShowError(t('common:errorMessage'));
            }
        } catch {
            ShowError(t('common:errorMessage'));
        } finally {
            showBulkUpdateDialog.setFalse();
        }
    };

    const validateBulkUpdate = async (data: SelectedFieldValuesProps[]) => {
        if (tableName === MonitoringToolKitTables.Observations) {
            // Only date field is closed date
            const closedDateUpdated = data.some((item) => item.type === 'date');
            if (closedDateUpdated) {
                const ids = selectionState.bulkUpdateRows.map((item) => item.id);
                const response = await HasClosedBeforeLatestReview(ids);
                if (response) {
                    setBulkUpdateFields(data);
                    showBulkUpdateWarningDialog.setTrue();
                    return;
                }
            }
        }
        await onSubmitBulkUpdate(data);
    };

    const [parentMenuActionItems, setParentMenuActionItems] = useState<RowMenuAction[]>(observationMenuActionItems);

    const onSubmitEdit = async (data: EditForm) => {
        try {
            const response = await updateObservation({
                closedDate: data.closedDate ? stringDateWithoutTimeFromLocal(data.closedDate) : undefined,
                observation: data.observation,
                comments: data.comments,
                rootCause: data.rootCause,
                ownerRef: data.owner?.ref,
                ownerName: data.owner?.fullName,
                observationId: observation?.id as unknown as number | undefined,
                observationDate: stringDateWithoutTimeFromLocal(data.observationDate!)!,
            });

            if (response?.success) {
                if (selectedParentChipHeader === ChipHeaders.ObservationsView) {
                    parentGridRef?.current?.refreshRow(response.id!.toString());
                }
                if (selectionState.selectedChildChipHeader === ChipHeaders.ObservationsView) {
                    childGridRef?.current?.refreshData();
                }
                ShowMessage({ text: t('actionsSuccessfulToast'), type: 'success' });
            } else {
                ShowError(t('common:errorMessage'));
            }
        } catch {
            ShowError(t('common:errorMessage'));
        } finally {
            showEditDialog.setFalse();
        }
    };

    const handleDelete = (success: boolean, conflict?: boolean) => {
        if (success) {
            // Refresh Parent Review Log grid as the child Observation could have many Review Logs which will be delete
            // We might need to keep filters
            if (
                selectedParentChipHeader === ChipHeaders.ReviewLogView ||
                selectedParentChipHeader === ChipHeaders.ActionView
            )
                parentGridRef?.current?.refreshData(false, true);
            else parentGridRef?.current?.removeRow(observation?.id as string, false);
            ShowMessage({ text: t('actionsSuccessfulToast'), type: 'success' });
            setSelectionState((old) => ({
                ...old,
                selectedField: '',
            }));
            showDeleteDialog.setFalse();
        } else if (conflict) {
            setDataRefreshDialogState({ show: true, onSubmit: () => showDeleteDialog.setFalse() });
        } else {
            ShowError(t('common:errorMessage'));
            showDeleteDialog.setFalse();
        }
    };
    const onSubmit = async (data: FormInputs) => {
        try {
            const response = await saveObservation({
                datasetOpenedOn: data.datasetOpenedOn?.id,
                associationType: EnumConverter(data.associationType),
                associationRef: data.associationType?.id as string,
                panelRef: data.panel?.reference,
                observationDate: stringDateWithoutTimeFromLocal(data.observationDate!)!,
                isSystemGenerated: false,
                type: ObservationType.Site,
                siteId: data.site?.id,
                ownerRef: data.owner?.ref,
                observation: data.observation,
                rootCause: data.rootCause,
                comments: data.comments,
                isScatterPlot: data.isScatterPlot,
                closedDate: data.closedDate ? stringDateWithoutTimeFromLocal(data.closedDate) : undefined,
            });

            if (response?.success) {
                parentGridRef?.current?.addRowAndSelect(response.id!.toString());
                ShowMessage({ text: t('actionsSuccessfulToast'), type: 'success' });
            } else {
                ShowError(t('common:errorMessage'));
            }
        } catch {
            ShowError(t('common:errorMessage'));
        } finally {
            showAddDialog.setFalse();
        }
    };

    const handleActionDelete = (success: boolean, conflict?: boolean) => {
        if (success) {
            if (selectedParentChipHeader === ChipHeaders.ObservationsView) {
                if (action) {
                    parentGridRef?.current?.refreshRow(action.observation_id);
                }
                childGridRef?.current?.removeRow(action?.id as string, false);
            } else if (selectedParentChipHeader === ChipHeaders.ReviewLogView) {
                childGridRef?.current?.removeRow(action?.id as string, false);
                // This should be true since we're looking at associated actions of this review log
                if (action?.review_log_id != null) parentGridRef?.current?.refreshRow(action?.review_log_id);
            } else if (selectedParentChipHeader === ChipHeaders.ActionView) {
                parentGridRef?.current?.removeRow(action?.id as string, false);
                setSelectionState((old) => ({
                    ...old,
                    selectedField: '',
                }));
            }
            ShowMessage({ text: t('actionsSuccessfulToast'), type: 'success' });
            showActionDeleteDialog.setFalse();
        } else if (conflict) {
            setDataRefreshDialogState({ show: true, onSubmit: () => showActionDeleteDialog.setFalse() });
        } else {
            ShowError(t('common:errorMessage'));
            showActionDeleteDialog.setFalse();
        }
    };

    const editReviewLog = async (data: EditReviewLogForm) => {
        try {
            const response = await updateReviewLog({
                reviewLogDataId: Number(reviewLog?.id),
                comments: data.comments,
                decision: data.decision?.name,
                decisionId: data.decision?.id,
                observationId: Number(reviewLog?.observation_id),
            });

            if (response.success) {
                ShowMessage({ text: t('common:actionsSuccessfulToast'), type: 'success' });

                if (selectedParentChipHeader === ChipHeaders.ReviewLogView) {
                    parentGridRef?.current?.refreshRow(response.id!.toString());

                    if (selectionState.selectedChildChipHeader === ChipHeaders.ReviewLogHistory)
                        childGridRef?.current?.refreshRow(response.id!.toString());
                }

                if (selectionState.selectedChildChipHeader === ChipHeaders.ReviewLogView) {
                    parentGridRef?.current?.refreshRow(reviewLog?.observation_id as unknown as string);
                    childGridRef?.current?.refreshRow(response.id!.toString());
                } else if (selectionState.selectedChildChipHeader === ChipHeaders.ObservationsView) {
                    childGridRef?.current?.refreshData();
                }
            } else {
                ShowError(t('common:errorMessage'));
            }
        } catch (ex) {
            ShowError(t('common:errorMessage'));
            window.console.error(ex);
        } finally {
            showEditReviewLogDialog.setFalse();
        }
    };

    const handleReviewLogDelete = (success: boolean, conflict?: boolean) => {
        if (success) {
            if (siteReviewLog) {
                // Refresh parent and child grids based on the selected chip headers
                if (selectedParentChipHeader === ChipHeaders.ObservationsView) {
                    parentGridRef?.current?.refreshRow(siteReviewLog.observation_id);
                } else if (selectedParentChipHeader === ChipHeaders.ActionView) {
                    parentGridRef?.current?.refreshData(false);
                } else if (selectedParentChipHeader === ChipHeaders.ReviewLogView) {
                    parentGridRef?.current?.refreshData(false);
                    parentGridRef?.current?.unselectRow(siteReviewLog.id);
                    setSelectionState((old) => ({
                        ...old,
                        selectedField: '',
                    }));
                }

                if (selectionState.selectedChildChipHeader === ChipHeaders.ObservationsView) {
                    childGridRef?.current?.refreshData();
                } else if (
                    selectionState.selectedChildChipHeader === ChipHeaders.ReviewLogView ||
                    selectionState.selectedChildChipHeader === ChipHeaders.ReviewLogHistory
                ) {
                    // Use refreshData instead of remove row, as menu actions need re-validating
                    childGridRef?.current?.refreshData(false);
                    childGridRef?.current?.unselectRow(siteReviewLog.id);
                }
            }

            ShowMessage({ text: t('actionsSuccessfulToast'), type: 'success' });
            showDeleteReviewLogDialog.setFalse();
        } else if (conflict) {
            setDataRefreshDialogState({ show: true, onSubmit: () => showDeleteReviewLogDialog.setFalse() });
        } else {
            ShowError(t('common:errorMessage'));
            showDeleteReviewLogDialog.setFalse();
        }
    };

    const isObservation = (
        selectedItem: ObservationRecord | Action | SiteReviewLog,
    ): selectedItem is ObservationRecord => {
        return 'open_actions' in selectedItem;
    };

    const onSelectionChange = (
        event: GridSelectionChangeEvent,
        selectedState: { [id: string]: boolean | number[] },
    ) => {
        let selectedRowCount = 0;
        let selectedItem: ObservationRecord | Action | SiteReviewLog;
        let selectedReviewLogId: string | null = null;

        isChildGridSelection.setFalse();

        let selectedItemsProps: SelectedRowsProps[] = [];
        if (Object.keys(selectedState).length !== 0) {
            const idGetter = getter('id') ?? '';

            const selectedRows = event.dataItems.filter((x) => selectedState[idGetter(x)] === true);
            selectedRowCount = selectedRows.length;
            selectedItemsProps = selectedRows.map((item: ObservationRecord) => ({
                id: idGetter(item) as number,
                hasOpenActions: item.open_actions > 0,
            }));
            selectedItem = event.dataItems.find((x) => selectedState[idGetter(x)] === true) as unknown as
                ObservationRecord | Action | SiteReviewLog;

            // Only need the review log id when the parent grid displays review logs
            if (selectedParentChipHeader === ChipHeaders.ReviewLogView && event.startDataItem) {
                selectedReviewLogId = event.startDataItem.id;
            }
        } else {
            selectedItem = event.startDataItem as ObservationRecord | Action | SiteReviewLog;
        }

        if (!selectedItem) {
            setSelectionState((old) => ({
                ...old,
                selectedField: '',
                selectedItemsCount: 0,
                selectedItemId: null,
                bulkUpdateRows: selectedItemsProps,
            }));

            return;
        }

        let selectedObservationId = '';
        let recordID: string | undefined;
        let reviewLogRecordID: string | undefined;
        let actionRecordId: string | undefined;
        if (tableName === MonitoringToolKitTables.Observations) {
            selectedObservationId = selectedItem.id as unknown as string;
            recordID = selectedItem.record_id;
        } else if (
            tableName === MonitoringToolKitTables.Actions ||
            tableName === MonitoringToolKitTables.SiteReviewLogs
        ) {
            selectedObservationId = selectedItem.observation_id as unknown as string;
            if (!isObservation(selectedItem) && selectedItem.observation_record_id)
                recordID = selectedItem.observation_record_id;
            reviewLogRecordID = selectedItem.record_id;
            actionRecordId = selectedItem.record_id;
        }

        if (isObservation(selectedItem)) {
            actionsCountRef.current = selectedItem.open_actions;
            reviewLogsCountRef.current = selectedItem.reviews;
        }

        let selectedActionId = '';
        if (tableName === MonitoringToolKitTables.Actions) {
            const selectedAction = selectedItem as Action;
            selectedActionId = selectedItem.id || '';
            if (selectedAction.review_log_id) selectedReviewLogId = selectedAction.review_log_id;
        }

        setSelectionState((old) => ({
            ...old,
            recordId: recordID,
            reviewLogRecordId: reviewLogRecordID,
            actionRecordId,
            selectedItemsCount: selectedRowCount,
            selectedItemId: selectedRowCount === 1 ? selectedItem.id : null,
            childGridParams: {
                ...old.childGridParams,
                studyRef,
                observationId: selectedObservationId,
                actionId: selectedActionId,
                reviewLogId: selectedReviewLogId,
                isChild: true,
                isScatterPlot: selectedItem.is_scatter_plot === 'Yes',
                isRateGroup: selectedItem.is_poisson_or_negative_binomial === 'Yes',
                isHistory: false,
                level: ObservationType.Site,
            },
            selectedField: selectedObservationId,
            bulkUpdateRows: selectedItemsProps,
        }));
    };

    const onChildSelectionChange = (
        event: GridSelectionChangeEvent,
        selectedState: { [id: string]: boolean | number[] },
    ) => {
        isChildGridSelection.setTrue();
        if (Object.keys(selectedState).length === 0) return;

        const idGetter = getter('id') ?? '';

        const selectedRows = event.dataItems.filter((x) => selectedState[idGetter(x)] === true);
        const selectedItemsProps: SelectedRowsProps[] = selectedRows.map((item: ObservationRecord) => ({
            id: idGetter(item) as number,
            hasOpenActions: item.open_actions > 0,
        }));
        setSelectionState((old) => ({
            ...old,
            bulkUpdateRows: selectedItemsProps,
        }));
    };

    const onParentChipSelected = (chipHeader: ChipHeaders): void => {
        switch (chipHeader) {
            case ChipHeaders.ObservationsView:
                setSelectionState((old) => ({
                    ...old,
                    selectedField: '',
                    childGridParams: {
                        ...old.childGridParams,
                        grid: 'ReviewLog',
                        level: ObservationType.Site,
                        observationId: old.selectedField,
                        reviewLogId: null,
                        isChild: true,
                        isHistory: false,
                    },
                    selectedChildChipHeader: ChipHeaders.ReviewLogView,
                    childMenuActionItems: reviewLogMenuActionItems,
                    selectedChildGridTitle: { id: ChipHeaders.ReviewLogView, label: 'Review Logs' },
                    bulkUpdateRows: [],
                }));
                setParentMenuActionItems(observationMenuActionItems(true));
                setExportFileName(
                    `Site Observations_${studyNumber}_${stringDateWithoutTimeFromCurrentUtcDate('DD-MMM-yyyy')}`,
                );
                setSelectedTitle({ id: ChipHeaders.ObservationsView, label: 'Observations' });
                setSelectedParentChipHeader(ChipHeaders.ObservationsView);
                parentChipRef.current = ChipHeaders.ObservationsView;
                childChipRef.current = ChipHeaders.ReviewLogView;
                setTableName(MonitoringToolKitTables.Observations);
                setParentRecordSelectMessage(t('cm:observationsGrid:selectObservationInfo'));
                setParentMultipleRecordSelectMessage(t('cm:observationsGrid:multipleSelectObservationInfo'));
                setUpdateParentGridParams((prevState) => ({
                    ...prevState,
                    grid: 'Observations',
                    level: ObservationType.Site,
                    observationId: null,
                    isChild: false,
                }));
                setSubPanelHeaders([
                    { id: ChipHeaders.ReviewLogView, label: 'Review Logs' },
                    {
                        id: ChipHeaders.ActionView,
                        label: 'Actions',
                    },
                ]);

                break;
            case ChipHeaders.ReviewLogView:
                setSelectionState((old) => ({
                    ...old,
                    selectedField: '',
                    childGridParams: {
                        ...old.childGridParams,
                        grid: 'Observations',
                        level: ObservationType.Site,
                        observationId: old.selectedField,
                        isChild: true,
                    },
                    selectedChildChipHeader: ChipHeaders.ObservationsView,
                    childMenuActionItems: observationMenuActionItems(false),
                    selectedChildGridTitle: { id: ChipHeaders.ObservationsView, label: 'Observation' },
                    bulkUpdateRows: [],
                }));
                setParentMenuActionItems(reviewLogMenuActionItems);
                setExportFileName(
                    `Site Review Logs_${studyNumber}_${stringDateWithoutTimeFromCurrentUtcDate('DD-MMM-yyyy')}`,
                );
                setSelectedTitle({ id: ChipHeaders.ReviewLogView, label: 'Review Logs' });
                setSelectedParentChipHeader(ChipHeaders.ReviewLogView);
                parentChipRef.current = ChipHeaders.ReviewLogView;
                childChipRef.current = ChipHeaders.ObservationsView;
                setTableName(MonitoringToolKitTables.SiteReviewLogs);
                setParentRecordSelectMessage(t('cm:reviewLogGrid:selectReviewLogInfo'));
                setParentMultipleRecordSelectMessage(t('cm:reviewLogGrid:multipleSelectReviewLogsInfo'));
                setUpdateParentGridParams((prevState) => ({
                    ...prevState,
                    grid: 'ReviewLog',
                    level: ObservationType.Site,
                    observationId: null,
                    reviewLogId: null,
                    isChild: false,
                    isHistory: false,
                }));
                setSubPanelHeaders([
                    { id: ChipHeaders.ObservationsView, label: 'Observation' },
                    {
                        id: ChipHeaders.ActionView,
                        label: 'Actions',
                    },
                    {
                        id: ChipHeaders.ReviewLogHistory,
                        label: 'Review Log History',
                    },
                ]);
                break;
            case ChipHeaders.ActionView:
                setSelectionState((old) => ({
                    ...old,
                    selectedField: '',
                    childGridParams: {
                        ...old.childGridParams,
                        grid: 'Observations',
                        level: ObservationType.Site,
                        observationId: old.selectedField,
                        actionId: old.selectedField,
                        reviewLogId: null,
                        isChild: true,
                    },
                    selectedChildChipHeader: ChipHeaders.ObservationsView,
                    childMenuActionItems: observationMenuActionItems(false),
                    selectedChildGridTitle: { id: ChipHeaders.ObservationsView, label: 'Observation' },
                    bulkUpdateRows: [],
                }));
                setExportFileName(
                    `Site Actions_${studyNumber}_${stringDateWithoutTimeFromCurrentUtcDate('DD-MMM-yyyy')}`,
                );
                setSelectedTitle({ id: ChipHeaders.ActionView, label: 'Actions' });
                setSelectedParentChipHeader(ChipHeaders.ActionView);
                parentChipRef.current = ChipHeaders.ActionView;
                childChipRef.current = ChipHeaders.ObservationsView;
                setParentMenuActionItems(actionMenuActionItems);
                setTableName(MonitoringToolKitTables.Actions);
                setParentRecordSelectMessage(t('cm:actionGrid:selectActionInfo'));
                setParentMultipleRecordSelectMessage(t('cm:actionGrid:multipleSelectActionsInfo'));
                setUpdateParentGridParams((prevState) => ({
                    ...prevState,
                    grid: 'Actions',
                    level: ObservationType.Site,
                    observationId: null,
                    reviewLogId: null,
                    isChild: false,
                }));
                setSubPanelHeaders([
                    { id: ChipHeaders.ObservationsView, label: 'Observation' },
                    {
                        id: ChipHeaders.ReviewLogView,
                        label: 'Review Logs',
                    },
                ]);
                break;
            default:
                setSelectionState((old) => ({
                    ...old,
                    selectedField: '',
                }));
                setSelectedParentChipHeader(ChipHeaders.ObservationsView);
                parentChipRef.current = ChipHeaders.ObservationsView;
                setUpdateParentGridParams((prevState) => ({
                    ...prevState,
                    grid: 'Observations',
                    level: ObservationType.Site,
                    observationId: null,
                    reviewLogId: null,
                    isChild: false,
                }));
        }
    };
    const onChildChipSelected = (chipHeader: ChipHeaders): void => {
        switch (chipHeader) {
            case ChipHeaders.ObservationsView:
                childChipRef.current = ChipHeaders.ObservationsView;
                setSelectionState((old) => ({
                    ...old,
                    childGridParams: {
                        ...old.childGridParams,
                        grid: 'Observations',
                        level: ObservationType.Site,
                        observationId: old.selectedField,
                        reviewLogId: 'reviewLogId' in old.childGridParams ? old.childGridParams.reviewLogId : null,
                        isChild: true,
                    },
                    selectedChildChipHeader: ChipHeaders.ObservationsView,
                    childMenuActionItems: observationMenuActionItems(false),
                    selectedChildGridTitle: { id: ChipHeaders.ObservationsView, label: 'Observation' },
                }));
                break;
            case ChipHeaders.ReviewLogView:
                childChipRef.current = ChipHeaders.ReviewLogView;
                setSelectionState((old) => ({
                    ...old,
                    childGridParams: {
                        ...old.childGridParams,
                        grid: 'ReviewLog',
                        level: ObservationType.Site,
                        observationId: old.selectedField,
                        reviewLogId: 'reviewLogId' in old.childGridParams ? old.childGridParams.reviewLogId : null,
                        isChild: true,
                        actionChild: selectedParentChipHeader === ChipHeaders.ActionView,
                        isHistory: false,
                    },
                    selectedChildChipHeader: ChipHeaders.ReviewLogView,
                    childMenuActionItems: reviewLogMenuActionItems,
                    selectedChildGridTitle: { id: ChipHeaders.ReviewLogView, label: 'Review Logs' },
                    childTableName: MonitoringToolKitTables.SiteReviewLogs,
                }));
                break;

            case ChipHeaders.ActionView:
                childChipRef.current = ChipHeaders.ActionView;
                setSelectionState((old) => ({
                    ...old,
                    childGridParams: {
                        ...old.childGridParams,
                        grid: 'Actions',
                        level: ObservationType.Site,
                        observationId: old.selectedField,
                        // Only for Review Log parent grid showing Actions child grid do we want to use the
                        // selected review log id as the filter for the actions. Everything else uses the observation
                        // id for the filter.
                        reviewLogId:
                            selectedParentChipHeader === ChipHeaders.ReviewLogView ? old.selectedItemId || null : null,
                        isChild: true,
                    },
                    selectedChildChipHeader: ChipHeaders.ActionView,
                    childMenuActionItems: actionMenuActionItems,
                    selectedChildGridTitle: { id: ChipHeaders.ActionView, label: 'Actions' },
                    childTableName: MonitoringToolKitTables.Actions,
                }));
                break;
            case ChipHeaders.ReviewLogHistory:
                childChipRef.current = ChipHeaders.ReviewLogHistory;
                setSelectionState((old) => ({
                    ...old,
                    childGridParams: {
                        ...old.childGridParams,
                        grid: 'ReviewLog',
                        level: ObservationType.Site,
                        observationId: old.selectedField,
                        reviewLogId: null,
                        isChild: true,
                        isHistory: true,
                    },
                    selectedChildChipHeader: ChipHeaders.ReviewLogHistory,
                    childMenuActionItems: undefined,
                    selectedChildGridTitle: { id: ChipHeaders.ReviewLogHistory, label: 'Review Log History' },
                    childTableName: MonitoringToolKitTables.SiteReviewLogs,
                }));
                break;
            default:
                childChipRef.current = ChipHeaders.ObservationsView;
                setSelectionState((old) => ({
                    ...old,
                    childGridParams: {
                        ...old.childGridParams,
                        grid: 'Observations',
                        level: ObservationType.Site,
                        observationId: old.selectedField,
                        reviewLogId: null,
                        isChild: true,
                    },
                    selectedChildChipHeader: ChipHeaders.ObservationsView,
                    selectedChildGridTitle: { id: ChipHeaders.ObservationsView, label: 'Observation' },
                }));
        }
    };

    const addAction = async (data: AddActionForm) => {
        if (selectionState.selectedChildChipHeader !== ChipHeaders.ActionView) {
            onChildChipSelected(ChipHeaders.ActionView);
        }
        try {
            const response = await saveAction({
                observationId: Number(data.observationId),
                ownerRef: data.owner?.ref,
                dueDate: data.dueDate ? stringDateWithoutTimeFromLocal(data.dueDate) : undefined,
                description: data.description,
                comments: data.comments,
                resolution: data.resolution,
                requestedDate: stringDateWithoutTimeFromLocal(data.requestedDate!)!,
                completedDate: data.completedDate ? stringDateWithoutTimeFromLocal(data.completedDate) : undefined,
                studyFunctionRef: data.responsibleFunction?.ref,
                siteReviewLogId: data.associatedReviewLog?.id,
                // When IntegrationActive is disabled for a study, then we don't need to save 0 or 1 for this field
                integrationEnabled: study?.integrationActive ? (data.integrationEnabled ? 1 : 0) : undefined,
            });

            if (response?.success) {
                ShowMessage({ text: t('common:actionsSuccessfulToast'), type: 'success' });
                parentGridRef?.current?.refreshRow(data.observationId!);
                childGridRef?.current?.addRowAndSelect(response.id!.toString());
                showAddActionDialog.setFalse();
            } else {
                ShowError(t('common:errorMessage'));
                showAddActionDialog.setFalse();
            }
        } catch {
            ShowError(t('common:errorMessage'));
            showAddActionDialog.setFalse();
        }
    };

    const editAction = async (data: EditActionForm) => {
        try {
            const integrationEnabled =
                action?.integration_enabled === IntegrationState.Undefined
                    ? 0
                    : action?.integration_enabled === IntegrationState.Enabled
                      ? 1
                      : action?.integration_enabled === IntegrationState.PushRequested
                        ? 2
                        : undefined;

            const response = await updateAction({
                actionId: Number(action?.id),
                dueDate: data.dueDate ? stringDateWithoutTimeFromLocal(data.dueDate) : undefined,
                description: data.description,
                comments: data.comments,
                resolution: data.resolution,
                requestedDate: stringDateWithoutTimeFromLocal(data.requestedDate!)!,
                completedDate: data.completedDate ? stringDateWithoutTimeFromLocal(data.completedDate) : undefined,
                ownerRef: data.owner?.ref,
                studyFunctionRef: data.responsibleFunction?.ref,
                siteReviewLogId: data.associatedReviewLog?.id,
                // When integration active is disabled at study level or integration enabled is already pushed then retain the old value
                integrationEnabled:
                    !study?.integrationActive || action?.integration_enabled === IntegrationState.PushRequested
                        ? integrationEnabled
                        : data.integrationEnabled
                          ? 1
                          : 0,
                configFileUploadTime: data.configFileUploadTime,
            });

            if (response.success) {
                ShowMessage({ text: t('common:actionsSuccessfulToast'), type: 'success' });
                const newAssociation = data.associatedReviewLog?.datasetDate || null;
                // Handle parent grid actions
                if (selectedParentChipHeader === ChipHeaders.ObservationsView && action) {
                    parentGridRef?.current?.refreshRow(action.observation_id);
                } else if (selectedParentChipHeader === ChipHeaders.ActionView) {
                    parentGridRef?.current?.refreshRow(response.id!.toString());

                    // If review log association changes then refresh the child grid
                    // This should either change/remove the review log
                    if (
                        selectionState.selectedChildChipHeader === ChipHeaders.ReviewLogView &&
                        action &&
                        action.associated_review_log !== newAssociation
                    ) {
                        childGridRef?.current?.refreshData(false);
                    }
                }

                // Handle child grid actions
                if (selectionState.selectedChildChipHeader === ChipHeaders.ActionView) {
                    if (
                        selectedParentChipHeader === ChipHeaders.ReviewLogView &&
                        action &&
                        action.associated_review_log !== newAssociation
                    ) {
                        // Refresh the parent grid with review logs (multiple rows may need updating)
                        parentGridRef?.current?.refreshData(false);
                        childGridRef?.current?.removeRow(response.id!.toString());
                    } else {
                        childGridRef?.current?.refreshRow(response.id!.toString());
                    }
                } else if (selectionState.selectedChildChipHeader === ChipHeaders.ObservationsView && action) {
                    childGridRef?.current?.refreshData();
                }

                showEditActionDialog.setFalse();
            } else {
                ShowError(t('common:errorMessage'));
            }

            showEditActionDialog.setFalse();
        } catch (err) {
            const status = (err as HTTPError)?.response?.status;

            if (status === 409) {
                // Conflict
                setDataRefreshDialogState({
                    show: true,
                    onSubmit: () => showEditActionDialog.setFalse(),
                });
            } else {
                ShowError(t('common:errorMessage'));
                showEditActionDialog.setFalse();
            }
        }
    };

    useEffect(() => {
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/set-state-in-effect
        setExportFileName(`Site Observations_${studyNumber}_${stringDateWithoutTimeFromCurrentUtcDate('DD-MMM-yyyy')}`);
    }, [studyNumber]);

    const [panes, setPanes] = useState<SplitterPaneProps[]>([{ size: '50%', min: '15px' }, { min: '35px' }]);
    const onPaneChange = (event: SplitterOnChangeEvent) => {
        debounce(() => setPanes(event.newState), 20);
    };
    const [gridCurrentView, setGridCurrentView] = useState<GridViewOptions>(SplitterOrientation.vertical);
    const [isReorderMode, setIsReorderMode] = useState(false);

    const addCallback = () => {
        if (selectedParentChipHeader === ChipHeaders.ObservationsView) showAddDialog.setTrue();
    };

    useEffect(() => {
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        /* eslint-disable @eslint-react/set-state-in-effect */
        setIsReorderMode(false);
        setGridCurrentView(SplitterOrientation.vertical);
        setUpdateParentGridParams((prevState) => ({
            ...prevState,
            studyRef,
        }));
        /* eslint-enable @eslint-react/set-state-in-effect */
    }, [studyRef]);
    const divHeight = isHalfScreen ? 'calc(50vh - 75px)' : 'calc(100vh - 128px)';

    const showEmailActions = useBoolean(false);
    const [emailPreviewData, setEmailPreviewData] = useState<ActionsEmailPreviewDto | null>(null);
    const { trigger: emailValidate, isMutating } = ActionsApi.useCanEmail(studyRef);
    const emailCallback = (selectedState: { [p: string]: boolean | number[] }) => {
        const selectedIds = Object.keys(selectedState).filter((k) => !!selectedState[k]);
        if (!selectedIds.length) return;

        emailValidate(selectedIds)
            .then((resp) => {
                setEmailPreviewData(resp);
                showEmailActions.setTrue();
            })
            .catch((e) => {
                window.console.error(e);
            });
    };

    const addReviewLog = async (data: AddReviewLog) => {
        const result = { success: false, responseId: null as string | null };
        if (selectionState.selectedChildChipHeader !== ChipHeaders.ReviewLogView) {
            onChildChipSelected(ChipHeaders.ReviewLogView);
        }
        try {
            const response = await saveReviewLog({
                observationId: Number(observation?.id),
                datasetId: Number(data.dataset.id),
                isScatterPlot: observation?.is_scatter_plot === 'Yes',
            });

            if (response?.success) {
                ShowMessage({ text: t('common:actionsSuccessfulToast'), type: 'success' });

                result.success = true;
                result.responseId = response.id!.toString();

                parentGridRef?.current?.refreshRow(observation!.id);
                // When virtualized, addRowAndSelect refreshes the gird
                childGridRef?.current?.addRowAndSelect(result.responseId);

                if (data.addAndReview) {
                    await handleReview(result.responseId);
                }
            } else {
                result.success = false;
                ShowError(t('common:errorMessage'));
            }
        } catch {
            result.success = false;
            ShowError(t('common:errorMessage'));
        } finally {
            showAddReviewLogDialog.setFalse();
        }
    };

    const integrationsOptions: (parentGrid: boolean) => IntegrationsConfig = (parentGrid: boolean) => [
        {
            onClick: async () => {
                let observationId: string | undefined;
                let logId: string | undefined;

                if (selectedParentChipHeader === ChipHeaders.ObservationsView)
                    observationId = selectionState.selectedItemId ?? undefined;
                if (selectedParentChipHeader === ChipHeaders.ReviewLogView)
                    logId = selectionState.selectedItemId ?? undefined;

                ShowMessage({ text: t('actionsSuccessfulToast'), type: 'success' });

                return pushIntegrations({ ByUserId: true, ObservationId: observationId, ReviewLogId: logId })
                    .then(() => {
                        return true;
                    })
                    .catch(() => {
                        return false;
                    });
            },
            disabled: !canEdit,
            label: t('cm:actionGrid.integrations.pushOwnerActions'),
            icon: ['far', 'arrow-right-long-to-line'] as IconProp,
        },
        ...(parentGrid
            ? [
                  {
                      onClick: async () => {
                          let observationId: string | undefined;
                          let logId: string | undefined;

                          if (selectedParentChipHeader === ChipHeaders.ObservationsView)
                              observationId = selectionState.selectedItemId ?? undefined;
                          if (selectedParentChipHeader === ChipHeaders.ReviewLogView)
                              logId = selectionState.selectedItemId ?? undefined;

                          ShowMessage({ text: t('actionsSuccessfulToast'), type: 'success' });

                          return pushIntegrations({ ObservationId: observationId, ReviewLogId: logId, ByUserId: false })
                              .then(() => {
                                  return true;
                              })
                              .catch(() => false);
                      },
                      disabled: !canEdit,
                      label: t('cm:actionGrid.integrations.pushActions'),
                      icon: ['far', 'arrow-right-long-to-line'] as IconProp,
                  },
              ]
            : []),
        {
            onClick: async () => {
                let observationId: string | undefined;
                let logId: string | undefined;

                if (selectedParentChipHeader === ChipHeaders.ObservationsView)
                    observationId = selectionState.selectedItemId ?? undefined;
                if (selectedParentChipHeader === ChipHeaders.ReviewLogView)
                    logId = selectionState.selectedItemId ?? undefined;

                ShowMessage({ text: t('actionsSuccessfulToast'), type: 'success' });

                return retryIntegrations({ ObservationId: observationId, ReviewLogId: logId, ByUserId: false })
                    .then(() => {
                        return true;
                    })
                    .catch(() => false);
            },
            disabled: !canEdit,
            label: t('cm:actionGrid.integrations.retryFailure'),
            icon: ['far', 'rotate-right'] as IconProp,
        },
    ];

    return (
        <div style={{ height: divHeight }}>
            {showEmailActions.value && (
                <EmailActionsDialog
                    studyRef={studyRef}
                    onClose={() => {
                        showEmailActions.setFalse();
                    }}
                    previewData={emailPreviewData}
                />
            )}
            <CoreSplitter
                className="!k-h-full"
                orientation={SplitterOrientation.vertical}
                data-testid="study-site-obs-splitter"
                alignmentValues={
                    gridCurrentView === GridFullView.full || isReorderMode
                        ? [
                              {
                                  size: '50%',
                              },
                              { size: '50%', collapsed: true },
                          ]
                        : panes
                }
                onChange={onPaneChange}
            >
                <div data-testid="site-observations-pane" className="k-h-full">
                    {studyRef && (
                        <ApiGrid
                            key={selectedTitle?.id ?? ''}
                            selectedTitle={selectedTitle}
                            rowMenuActions={parentMenuActionItems}
                            title={mainPanelHeaders}
                            gridParams={updateParentGridParams}
                            table={tableName}
                            maxHeight="calc(100vh)"
                            testDataId="site-observations-grid"
                            onChipHeaderSelected={onParentChipSelected}
                            selectedChipHeader={selectedParentChipHeader}
                            addCallback={addCallback}
                            canEdit={canEdit}
                            noPadding
                            selectable={{ enabled: true, mode: 'multiple', drag: true }}
                            onSelectionChange={onSelectionChange}
                            dataItemKey="id"
                            exportFilename={exportFileName}
                            showSendEmailIcon={tableName === MonitoringToolKitTables.Actions}
                            showAddIcon={tableName === MonitoringToolKitTables.Observations}
                            ref={parentGridRef}
                            showBulkUpdateIcon
                            bulkUpdateTooltip={
                                tableName === MonitoringToolKitTables.Observations
                                    ? t('cm:observationsGrid.editObservations')
                                    : tableName === MonitoringToolKitTables.SiteReviewLogs
                                      ? t('cm:reviewLogGrid.editReviewLogs')
                                      : tableName === MonitoringToolKitTables.Actions
                                        ? t('cm:actionGrid.editActions')
                                        : undefined
                            }
                            addRecordTooltip={
                                tableName === MonitoringToolKitTables.Observations
                                    ? t('cm:observationsGrid.addObservation')
                                    : undefined
                            }
                            gridStatusSetup={gridStatusSetup}
                            filterListener={(newFilterState) => {
                                parentFilterRef.current = newFilterState || null;
                            }}
                            emailCallback={emailCallback}
                            loading={isMutating && parentChipRef.current === ChipHeaders.ActionView}
                            onBulkUpdateClicked={handleOnBulkUpdateClicked}
                            useFilterWarning={selectionState.bulkUpdateRows.length > 0}
                            showPlaceholder={false}
                            scrollable="virtual"
                            gridRefresh={
                                parentGridRef.current ? () => parentGridRef.current?.refreshData(false) : undefined
                            }
                            integrationsOptions={
                                selectedParentChipHeader === ChipHeaders.ActionView &&
                                study?.integrationActive &&
                                hasIntegrationConfig?.hasIntegrationConfig === true
                                    ? integrationsOptions(true)
                                    : undefined
                            }
                        />
                    )}
                </div>
                <div className="k-h-full">
                    {selectionState.selectedField && selectionState.selectedItemsCount === 1 && (
                        <ApiGrid
                            key={selectionState.selectedChildChipHeader + selectionState.selectedField}
                            title={subPanelHeaders}
                            selectedTitle={selectionState.selectedChildGridTitle}
                            gridParams={selectionState.childGridParams}
                            table={selectionState.childTableName}
                            rowMenuActions={selectionState.childMenuActionItems}
                            maxHeight="auto"
                            canEdit={canEdit}
                            testDataId="site-observations-child-grid"
                            onChipHeaderSelected={onChildChipSelected}
                            selectedChipHeader={selectionState.selectedChildChipHeader}
                            exportFilename={getExportChildFileName(
                                selectionState.childGridParams.grid,
                                studyNumber,
                                selectionState.selectedChildChipHeader,
                                selectedParentChipHeader,
                                selectionState.recordId,
                                selectionState.reviewLogRecordId,
                                selectionState.actionRecordId,
                            )}
                            onSelectionChange={onChildSelectionChange}
                            bulkUpdateTooltip={
                                selectionState.childTableName === MonitoringToolKitTables.Observations
                                    ? t('cm:observationsGrid.editObservations')
                                    : selectionState.childTableName === MonitoringToolKitTables.SiteReviewLogs
                                      ? t('cm:reviewLogGrid.editReviewLogs')
                                      : selectionState.childTableName === MonitoringToolKitTables.Actions
                                        ? t('cm:actionGrid.editActions')
                                        : undefined
                            }
                            showSendEmailIcon={selectionState.selectedChildGridTitle?.id === ChipHeaders.ActionView}
                            showBulkUpdateIcon={selectionState.selectedChildGridTitle?.id === ChipHeaders.ActionView}
                            showAddIcon={false}
                            filterable={
                                selectionState.selectedChildGridTitle?.id === ChipHeaders.ReviewLogHistory ||
                                selectionState.selectedChildGridTitle?.id === ChipHeaders.ActionView
                            }
                            ref={childGridRef}
                            dataItemKey="id"
                            selectable={{ enabled: true, mode: 'multiple', drag: true }}
                            filterListener={(newFilterState) => {
                                childFilterRef.current = newFilterState || null;
                            }}
                            emailCallback={emailCallback}
                            loading={isMutating && childChipRef.current === ChipHeaders.ActionView}
                            isChildGrid
                            onBulkUpdateClicked={handleOnChildUpdateClicked}
                            useFilterWarning={selectionState.bulkUpdateRows.length > 0}
                            showPlaceholder={false}
                            noPadding
                            scrollable="virtual"
                            gridRefresh={
                                childGridRef.current ? () => childGridRef.current?.refreshData(false) : undefined
                            }
                            integrationsOptions={
                                childChipRef.current === ChipHeaders.ActionView &&
                                study?.integrationActive &&
                                hasIntegrationConfig?.hasIntegrationConfig === true
                                    ? integrationsOptions(false)
                                    : undefined
                            }
                        />
                    )}
                    {selectionState.selectedItemsCount > 1 && (
                        <InfoField type="Info" message={parentMultipleRecordSelectMessage} layout="center" />
                    )}
                    {(!selectionState.selectedField || selectionState.selectedItemsCount === 0) && (
                        <InfoField type="Info" message={parentRecordSelectMessage} layout="center" />
                    )}
                </div>
            </CoreSplitter>
            {showFilterAppliedDialog.value && (
                <ConfirmationDialog
                    dataTestId="confirm-add-study-observation"
                    title={t('grid.filterSortWarning.title')}
                    onSubmit={() => {
                        showFilterAppliedDialog.setFalse();
                    }}
                    onClose={() => {
                        showAddDialog.setFalse();
                        showAddActionDialog.setFalse();
                        showEditDialog.setFalse();
                        showEditActionDialog.setFalse();
                        showEditReviewLogDialog.setFalse();
                        showDeleteDialog.setFalse();
                        showActionDeleteDialog.setFalse();
                        showFilterAppliedDialog.setFalse();
                        showDeleteReviewLogDialog.setFalse();
                        showAddReviewLogDialog.setFalse();
                        showBulkUpdateDialog.setFalse();
                    }}
                    primaryBtnLabel={t('common:continue')}
                >
                    {t('grid.filterSortWarning.body')}
                </ConfirmationDialog>
            )}
            {showAddDialog.value && !showFilterAppliedDialog.value && (
                <AddSiteObservationDialog
                    studyRef={studyRef}
                    orgKey={orgKey}
                    onSubmit={onSubmit}
                    onClose={onAddClose}
                />
            )}
            {showAddActionDialog.value && !showFilterAppliedDialog.value && observation && (
                <AddActionDialog
                    observationId={observation.id}
                    studyRef={studyRef}
                    onSubmit={addAction}
                    onClose={showAddActionDialog.setFalse}
                    type={ObservationType.Site}
                />
            )}
            {showEditDialog.value && !showFilterAppliedDialog.value && observation && (
                <EditSiteObservationDialog
                    studyRef={studyRef}
                    onSubmit={onSubmitEdit}
                    onClose={onEditClose}
                    observation={observation}
                />
            )}
            {showActionDeleteDialog.value && !showFilterAppliedDialog.value && (
                <DeleteActionDialog
                    recordId={action?.record_id ?? ''}
                    onClose={showActionDeleteDialog.setFalse}
                    onDeleteHandler={handleActionDelete}
                    studyRef={studyRef}
                    observationId={action?.observation_id}
                    actionId={action?.id as unknown as number}
                    type={ObservationType.Site}
                />
            )}
            {showEditActionDialog.value && !showFilterAppliedDialog.value && action && (
                <EditActionDialog
                    action={action}
                    studyRef={studyRef}
                    onSubmit={editAction}
                    onClose={showEditActionDialog.setFalse}
                    type={ObservationType.Site}
                />
            )}
            {showDeleteDialog.value && !showFilterAppliedDialog.value && (
                <DeleteObservationDialog
                    recordId={selectionState.recordId ?? ''}
                    actionsCount={actionsCountRef.current}
                    reviewLogsCount={reviewLogsCountRef.current}
                    onClose={onDeleteClose}
                    onDeleteHandler={handleDelete}
                    studyRef={studyRef}
                    observationId={observation?.id as unknown as number}
                    observation={observation?.observation}
                    siteName={observation?.site_name}
                    dataSetDate={stringDateWithoutTimeFromUtc(observation?.dataset_opened_on, 'DD-MMM-YYYY')}
                    type={ObservationType.Site}
                />
            )}
            {showEditReviewLogDialog.value && !showFilterAppliedDialog.value && reviewLog && (
                <EditSiteReviewLogDialog
                    reviewLog={reviewLog}
                    studyRef={studyRef}
                    onSubmit={editReviewLog}
                    onClose={showEditReviewLogDialog.setFalse}
                />
            )}
            {showDeleteReviewLogDialog.value && !showFilterAppliedDialog.value && (
                <DeleteReviewLogDialog
                    recordId={siteReviewLog?.record_id ?? ''}
                    reviewLogId={siteReviewLog?.id as unknown as number}
                    studyRef={studyRef}
                    observationId={siteReviewLog && siteReviewLog.observation_id}
                    onClose={showDeleteReviewLogDialog.setFalse}
                    onDeleteHandler={handleReviewLogDelete}
                    type={ObservationType.Site}
                    reviewLogDatasetDate={siteReviewLog?.dataset}
                />
            )}
            {showBulkUpdateDialog.value && !showFilterAppliedDialog.value && (
                <BulkUpdateDialog
                    studyRef={studyRef}
                    onClose={() => {
                        showBulkUpdateDialog.setFalse();
                    }}
                    onSubmit={validateBulkUpdate}
                    dialogTitle={
                        (!isChildGridSelection.value && tableName === MonitoringToolKitTables.SiteReviewLogs) ||
                        (isChildGridSelection.value &&
                            selectionState.childTableName === MonitoringToolKitTables.SiteReviewLogs)
                            ? t('cm:bulkUpdateDialog.dialogTitle.reviewLogs')
                            : (!isChildGridSelection.value && tableName === MonitoringToolKitTables.Actions) ||
                                (isChildGridSelection.value &&
                                    selectionState.childTableName === MonitoringToolKitTables.Actions)
                              ? t('cm:bulkUpdateDialog.dialogTitle.actions')
                              : t('cm:bulkUpdateDialog.dialogTitle.site')
                    }
                    warningMessage={
                        (!isChildGridSelection.value && tableName === MonitoringToolKitTables.SiteReviewLogs) ||
                        (isChildGridSelection.value &&
                            selectionState.childTableName === MonitoringToolKitTables.SiteReviewLogs)
                            ? t('cm:bulkUpdateDialog.warnings.reviewLogs', {
                                  count: selectionState.bulkUpdateRows.length,
                              })
                            : (!isChildGridSelection.value && tableName === MonitoringToolKitTables.Actions) ||
                                (isChildGridSelection.value &&
                                    selectionState.childTableName === MonitoringToolKitTables.Actions)
                              ? t('cm:bulkUpdateDialog.warnings.siteActions', {
                                    count: selectionState.bulkUpdateRows.length,
                                })
                              : t('cm:bulkUpdateDialog.warnings.study', { count: selectionState.bulkUpdateRows.length })
                    }
                    fields={
                        (!isChildGridSelection.value && tableName === MonitoringToolKitTables.SiteReviewLogs) ||
                        (isChildGridSelection.value &&
                            selectionState.childTableName === MonitoringToolKitTables.SiteReviewLogs)
                            ? ReviewLogFields
                            : (!isChildGridSelection.value && tableName === MonitoringToolKitTables.Actions) ||
                                (isChildGridSelection.value &&
                                    selectionState.childTableName === MonitoringToolKitTables.Actions)
                              ? actionFields
                              : observationFields
                    }
                    unsavedChangesArea="central-monitoring-toolkit"
                />
            )}
            {showBulkUpdateWarningDialog.value && (
                <ConfirmationDialog
                    title={t('cm:bulkUpdateDialog.closedSystemObservationWarningTitle')}
                    dataTestId="confirmation-dialog"
                    onClose={() => showBulkUpdateWarningDialog.setFalse()}
                    onSubmit={async () => {
                        showBulkUpdateWarningDialog.setFalse();
                        await onSubmitBulkUpdate(bulkUpdateFields);
                    }}
                    primaryBtnDisabled={false}
                    ignoreReadonlyCtx
                    className="k-z-1010"
                >
                    <div className={styles.warnings}>
                        <div className={styles.bulkUpdate}>
                            <div className={styles.systemObsClosedDateWarning}>
                                <InfoField
                                    type="Warning"
                                    message={
                                        <div className={styles.infoFieldMessage}>
                                            {t('cm:bulkUpdateDialog.closedSystemObservationWarning1')}
                                        </div>
                                    }
                                />
                                <div className={styles.continueMessage}>
                                    {t('cm:bulkUpdateDialog.closedSystemObservationWarningContinue')}
                                </div>
                            </div>
                        </div>
                    </div>
                </ConfirmationDialog>
            )}
            {showAddReviewLogDialog.value && !showFilterAppliedDialog.value && observation && (
                <AddReviewLogDialog
                    observationId={observation.id}
                    studyRef={studyRef}
                    onSubmit={addReviewLog}
                    onClose={showAddReviewLogDialog.setFalse}
                    level={ObservationType.Site}
                />
            )}
            {dataRefreshDialogState.show && (
                <ConfirmationDialog
                    dataTestId="data-refresh-dialog"
                    title={t('cm:refreshDataDialog.title')}
                    onSubmit={() => {
                        dataRefreshDialogState.onSubmit();
                        setDataRefreshDialogState({
                            show: false,
                            onSubmit: () => {},
                        });
                    }}
                    onClose={() => {
                        setDataRefreshDialogState({
                            show: false,
                            onSubmit: () => {},
                        });
                    }}
                    primaryBtnLabel={t('common:continue')}
                    ignoreReadonlyCtx
                    width={559}
                >
                    <div className={styles.refreshDialog}>
                        <InfoField type="Warning" message={t('cm:refreshDataDialog.integrationWarning')} />
                        <div className={styles.bodyText}>{t('cm:refreshDataDialog.body')}</div>
                    </div>
                </ConfirmationDialog>
            )}
        </div>
    );
};

export default SiteObservations;
